import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { MyRouterContext } from '@/App';
import { LoaderFnContext } from '@tanstack/react-router/dist/esm/route';
import { logger } from '@/lib/logger';
import { MainContainer } from '@/components/MainContainer';
import { PageHeader } from '@/components/PageHeader';
import { Anchor, Box, Button, Stepper, Text, Title } from '@mantine/core';
import { suppress404 } from '@/lib/zodius';
import { useState } from 'react';
import { notifyError } from '@/lib/notification';
import { modals } from '@mantine/modals';
import { CodeHighlight } from '@mantine/code-highlight';
import { IconKey } from '@tabler/icons-react';
import { isPersonalTenant } from '@/lib/auth';

export const Route = createFileRoute('/_main/_project/welcome')({
  loader,
  component: Component,
});

async function loader({
  context: { adminClient },
}: LoaderFnContext<Record<never, string>, never, MyRouterContext>) {
  const apiKey = await suppress404(adminClient.settings_v1_get_personal_api_key());
  return {
    apiKey,
    adminClient,
  };
}

function Component() {
  // 初回ログイン限定の、APIキー発行の Getting Started ページです
  const { apiKey, adminClient } = Route.useLoaderData();
  const { userInfo } = Route.useRouteContext();
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);

  // handlers
  const handleGenerateApikey = async () => {
    setLoading(true);
    try {
      const res = await adminClient.settings_v1_generate_personal_api_key(undefined);
      await router.load();
      openGenerateApiKeyResult(res.api_key);
    } catch (e) {
      logger.error(e);
      notifyError('シークレットの発行に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  // modals
  const openConfirmRegenerateApiKey = () => {
    modals.openConfirmModal({
      title: 'APIキーのシークレットを再発行しますか？',
      children: (
        <div className="text-sm">
          この操作により新しいシークレットが作成され、現在のシークレットは無効になります。
        </div>
      ),
      onConfirm: () => handleGenerateApikey(),
    });
  };

  const openGenerateApiKeyResult = (secret: string) => {
    modals.open({
      title: 'APIキーのシークレットが発行されました',
      children: (
        <>
          <div className="text-sm">
            シークレットは、この画面を閉じると再表示できません。安全な場所に保存してください。
          </div>
          <div className="my-10">
            <div className="text-sm mb-1">APIキーのシークレット</div>
            <CodeHighlight
              classNames={{ root: 'border border-plamo-border rounded-lg' }}
              code={secret}
            ></CodeHighlight>
          </div>
        </>
      ),
    });
  };

  return (
    <MainContainer>
      <PageHeader title="PLaMo APIへようこそ！" />
      <Title order={4}>API キーを発行して、PLaMo API を使ってみましょう。</Title>
      <Box mt="30px" mx="20px">
        <Stepper active={active} onStepClick={setActive} orientation="vertical">
          <Stepper.Step
            label="個人用の API キーを発行しましょう"
            description={
              <Box mt="sm" mb="xl">
                <Text>以下のボタンをクリックして、APIキーを発行してください。</Text>
                <Button
                  color="blue"
                  size="sm"
                  leftSection={<IconKey style={{ width: '70%', height: '70%' }} />}
                  onClick={async () => {
                    if (apiKey) {
                      openConfirmRegenerateApiKey();
                    } else {
                      await handleGenerateApikey();
                    }
                  }}
                  className="mt-3"
                  loading={loading}
                >
                  {apiKey ? 'API キーを再発行する' : 'API キーを発行する'}
                </Button>
              </Box>
            }
          />
          <Stepper.Step
            label="API キーを使ってリクエストを送信しましょう"
            description={
              <Box mt="sm" mb="xl">
                <Text>以下のコマンドを実行してください。</Text>
                <CodeHighlight
                  classNames={{ root: 'border border-plamo-border rounded-lg' }}
                  code={requestExample}
                />
              </Box>
            }
          />
          <Stepper.Step
            label="アプリケーションに組み込んでみましょう"
            description={
              <Box mt="sm" mb="xl">
                <Anchor href="https://docs.plamo.preferredai.jp/ja/getting-started" target="_blank">
                  PLaMo リファレンスの API 利用方法
                </Anchor>
                を参考に、アプリケーションに PLaMo API を組み込んでみましょう。
              </Box>
            }
          />
          {/* 無料プランにはプロジェクト単位のキー管理がない */}
          {!isPersonalTenant(userInfo) && (
            <Stepper.Step
              label="準備完了です！"
              description={
                <Box mt="sm" mb="xl">
                  複数の API キーを発行したい、API
                  キーごとのトークン利用量を確認したい、チームで管理をしたい、という場合は
                  <br />
                  <Anchor href="https://docs.plamo.preferredai.jp/ja/console" target="_blank">
                    コンソールマニュアル
                  </Anchor>
                  を参考にプロジェクトベースの API キー管理を行ってください。
                  <Anchor component={Link} to="/tenant/projects">
                    <Text mt="xl">プロジェクト管理 に移動する</Text>
                  </Anchor>
                </Box>
              }
            />
          )}
        </Stepper>
      </Box>
    </MainContainer>
  );
}

const requestExample = `
export PLAMO_API_KEY="your_api_key"
curl -H "Authorization: Bearer \${PLAMO_API_KEY}" \\
     -H "Content-Type: application/json" \\
     "https://platform.preferredai.jp/api/completion/v1/chat/completions" \\
     -d @- << EOF
{
  "messages": [
    {
      "role": "system",
      "content": "あなたは旅行アドバイザーです"
    },
    {
      "role": "user",
      "content": "金沢で朝から夕方まで1日のおすすめの観光ルートを教えて下さい"
    }
  ],
  "model": "plamo-2.0-prime"
}
EOF
`;
